import "./preset-bar.css";

import { Dropdown, type DropdownOption } from "./dropdown.ts";
import { SynthButton } from "./button.ts";
import { showAlert, showConfirm, showPrompt } from "./modal.ts";
import type { PresetManager } from "../preset/manager.ts";
import type { Preset } from "../preset/preset.ts";

// PresetBar — small UI sitting above the synth panel modules. Owns
// three jobs:
//   • Dropdown — picks any factory or user preset, applies it on
//     selection. Re-rendered whenever the manager's preset list
//     changes (new save, delete).
//   • Save button — overwrites the currently-loaded user preset, or
//     prompts for a name if the loaded preset is a factory (since
//     factories are read-only).
//   • Save-as button — always prompts for a name (uses `window.prompt`
//     for simplicity; a proper inline rename UI is a polish target).
//
// The "dirty" affordance is a small amber asterisk after the preset
// name in the bar — appears whenever the user changes a control after
// the last preset load and disappears on the next save.

export interface PresetBarOptions {
  container: HTMLElement;
  manager: PresetManager;
}

export class PresetBar {
  private root: HTMLDivElement;
  private currentLabel: HTMLElement;
  private dropdown!: Dropdown<string>;
  private dropdownHost: HTMLDivElement;
  private deleteBtn!: SynthButton;
  private manager: PresetManager;
  private unsubscribe: () => void;

  constructor(opts: PresetBarOptions) {
    this.manager = opts.manager;

    this.root = document.createElement("div");
    this.root.className = "preset-bar";

    const label = document.createElement("span");
    label.className = "preset-bar-label";
    label.textContent = "preset";
    this.root.append(label);

    this.currentLabel = document.createElement("span");
    this.currentLabel.className = "preset-bar-current";
    this.root.append(this.currentLabel);

    const controls = document.createElement("div");
    controls.className = "preset-bar-controls";
    this.root.append(controls);

    this.dropdownHost = document.createElement("div");
    controls.append(this.dropdownHost);
    this.rebuildDropdown();

    // Save — overwrites the currently-named user preset, or falls
    // through to save-as if a factory is loaded.
    const saveSlot = document.createElement("div");
    controls.append(saveSlot);
    new SynthButton({
      container: saveSlot,
      label: "save",
      variant: "momentary",
      onPress: () => this.onSave(),
    });

    // Save-as — always prompts for a name.
    const saveAsSlot = document.createElement("div");
    controls.append(saveAsSlot);
    new SynthButton({
      container: saveAsSlot,
      label: "save as…",
      variant: "momentary",
      onPress: () => this.onSaveAs(),
    });

    // Delete — only enabled when a user preset is loaded. Factories are
    // read-only and can't be removed; greying the button instead of
    // hiding it keeps the bar's layout stable across preset changes.
    const deleteSlot = document.createElement("div");
    controls.append(deleteSlot);
    this.deleteBtn = new SynthButton({
      container: deleteSlot,
      label: "delete",
      variant: "momentary",
      intent: "red",
      onPress: () => this.onDelete(),
    });

    opts.container.append(this.root);

    this.unsubscribe = this.manager.subscribe(() => this.refresh());
    this.refresh();
  }

  destroy(): void {
    this.unsubscribe();
    this.root.remove();
  }

  // ----- actions -----

  private async onSave(): Promise<void> {
    const name = this.manager.currentPresetName();
    // Factory presets are read-only — fall through to save-as so the
    // user gets a fresh user preset instead of silently failing.
    if (this.isFactoryName(name)) {
      await this.onSaveAs();
      return;
    }
    this.manager.saveAs(name);
  }

  private async onSaveAs(): Promise<void> {
    const fallback = this.manager.currentPresetName() || "my preset";
    const raw = await showPrompt({
      title: "save preset",
      message: "give your preset a name.",
      defaultValue: fallback,
      placeholder: "preset name",
      confirmLabel: "save",
    });
    if (raw === null) return;
    const trimmed = raw.trim();
    if (!trimmed) return;
    // Bail with a visible message instead of silently no-op'ing when
    // the user picks a name that collides with a factory — otherwise
    // the click feels broken.
    if (this.isFactoryName(trimmed)) {
      await showAlert({
        title: "name already in use",
        message: `"${trimmed}" is a factory preset name. choose a different name for your user preset.`,
      });
      return;
    }
    this.manager.saveAs(trimmed);
  }

  private async onDelete(): Promise<void> {
    const name = this.manager.currentPresetName();
    if (this.isFactoryName(name) || !name) return;
    const ok = await showConfirm({
      title: "delete preset",
      message: `delete "${name}"? this can't be undone.`,
      confirmLabel: "delete",
      cancelLabel: "keep",
      destructive: true,
    });
    if (!ok) return;
    this.manager.delete(name);
    // After delete the bar would still show the (now-orphaned) name —
    // fall back to the first factory (`init`) so the panel state and
    // the bar label both reflect a real, loadable preset.
    const factory = this.manager
      .list()
      .find((e) => e.source === "factory");
    if (factory) this.manager.apply(factory.preset);
  }

  // ----- view -----

  private rebuildDropdown(): void {
    // Tear down the existing dropdown and rebuild — Dropdown's
    // `setOptions` would keep the selection but we also want any
    // newly-added user presets to appear, ordered factories-then-user.
    this.dropdownHost.replaceChildren();
    const entries = this.manager.list();
    const options: DropdownOption<string>[] = entries.map((e) => ({
      value: keyForPreset(e.preset, e.source),
      label: e.preset.name,
      hint: e.source === "factory" ? "factory" : undefined,
    }));
    const currentKey = this.dropdownKeyForCurrent();
    this.dropdown = new Dropdown<string>({
      container: this.dropdownHost,
      options,
      value: currentKey,
      minWidth: 200,
      onChange: (key) => {
        const target = entries.find(
          (e) => keyForPreset(e.preset, e.source) === key,
        );
        if (target) this.manager.apply(target.preset);
      },
    });
  }

  private refresh(): void {
    const name = this.manager.currentPresetName();
    this.currentLabel.textContent = name;
    this.currentLabel.classList.toggle("is-dirty", this.manager.isDirty());

    // Delete is only meaningful for user presets — factories are
    // read-only. Greying the button (rather than hiding) keeps the
    // bar's layout stable across selection changes.
    this.deleteBtn.setDisabled(this.isFactoryName(name) || !name);

    // If the dropdown's set of options has changed (save / delete) we
    // need to rebuild it. Use option count + currentValue mismatch as
    // a cheap dirty check.
    const expected = this.manager.list().length;
    if (this.optionsCount() !== expected) {
      this.rebuildDropdown();
    } else {
      // Just sync the selection.
      const key = this.dropdownKeyForCurrent();
      if (this.dropdown && this.dropdown.getValue() !== key && key) {
        this.dropdown.setValue(key);
      }
    }
  }

  private optionsCount(): number {
    return this.dropdownHost.querySelectorAll(".dropdown-item").length;
  }

  private dropdownKeyForCurrent(): string {
    const name = this.manager.currentPresetName();
    const entries = this.manager.list();
    const match = entries.find((e) => e.preset.name === name);
    if (!match) return "";
    return keyForPreset(match.preset, match.source);
  }

  private isFactoryName(name: string): boolean {
    return this.manager.list().some(
      (e) => e.source === "factory" && e.preset.name === name,
    );
  }
}

/** Stable id for a preset entry. Factories and user presets sharing a
 *  display name still map to distinct dropdown keys, since user-saved
 *  ones get a "user:" prefix. */
function keyForPreset(p: Preset, source: "factory" | "user"): string {
  return `${source}:${p.name}`;
}
